import { Actions, createEffect, ofType } from '@ngrx/effects'
import { filter, map, switchMap, withLatestFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { Store } from '@ngrx/store'
import { ChangeRequestActions } from '../actions/change-request.actions'
import { ChangeRequestSelectors } from '../selectors/change-request.selectors'
import { ChangeItemType, ChangeType } from '../../model'
import { ScopePaginatedOverviewActions } from '@app/features/scope-overview/store/actions/scope-paginated-overview.actions'
import { mapChangeRequestDeliverables } from '../../util/map-change-request-deliverables.util'
import { ChangeRequestDeliverableActions } from '../actions/change-request-deliverable.actions'

@Injectable()
export class ChangeRequestDeliverableEffects {
  constructor(
    private store$: Store,
    private actions$: Actions
  ) {}

  fetchDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.fetchDeliverable),
      withLatestFrom(this.store$.select(ChangeRequestSelectors.selectPreviewDeliverablesMap)),
      filter(
        ([{ deliverableId }, deliverablesMap]) =>
          !!(deliverableId && deliverablesMap?.[deliverableId]?.changeAuditData?.identity)
      ),
      map(([payload]) => ScopePaginatedOverviewActions.getScopeDeliverable(payload))
    )
  )

  modifyDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.modifyDeliverable),
      map(({ identity, changes }) =>
        ChangeRequestActions.applyChanges({
          payload: {
            changeType: ChangeType.MODIFIED,
            type: ChangeItemType.DELIVERABLE,
            identity,
            data: changes,
          },
        })
      )
    )
  )

  deleteDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.deleteDeliverable),
      withLatestFrom(
        this.store$.select(ChangeRequestSelectors.selectChangeItemEntities),
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ChangeRequestSelectors.selectCurrentChangeRequest)
      ),
      switchMap(([{ deliverableId }, changeItemEntities, currentScope, currentChangeRequest]) => [
        ChangeRequestActions.removeChanges({ changeItemUuid: changeItemEntities?.[deliverableId]?.uuid }),
        ChangeRequestActions.createChanges({
          scopeId: currentScope.identity.id,
          changeRequestId: currentChangeRequest.id,
          payload: {
            changeType: ChangeType.REMOVED,
            type: ChangeItemType.DELIVERABLE,
            identity: deliverableId,
          },
        }),
      ])
    )
  )

  revertDeletedDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.revertDeletedDeliverable),
      withLatestFrom(this.store$.select(ChangeRequestSelectors.selectChangeItemEntities)),
      map(([{ deliverableId }, changeItemEntities]) =>
        ChangeRequestActions.removeChanges({ changeItemUuid: changeItemEntities?.[deliverableId]?.uuid })
      )
    )
  )

  addDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.addDeliverable),
      map(({ data }) =>
        ChangeRequestActions.applyChanges({
          payload: {
            changeType: ChangeType.ADDED,
            type: ChangeItemType.DELIVERABLE,
            identity: data.id,
            data: { data },
          },
        })
      )
    )
  )

  mapDeliverables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ChangeRequestActions.getChangeRequestPreviewSuccess,
        ScopePaginatedOverviewActions.showNextBufferedDeliverables
      ),
      withLatestFrom(
        this.store$.select(ChangeRequestSelectors.selectCurrentChangeRequest),
        this.store$.select(ScopeOverviewSelectors.selectDisplayedDeliverables),
        this.store$.select(ChangeRequestSelectors.selectPreviewDeliverables),
        this.store$.select(ChangeRequestSelectors.selectPreviewDeliverablesMap),
        this.store$.select(ChangeRequestSelectors.selectChangeItemEntities)
      ),
      filter(([_, currentChangeRequest]) => !!currentChangeRequest),
      switchMap(([, _, deliverables, previewDeliverables, previewDeliverablesMap, changeItemsMap]) => [
        ChangeRequestDeliverableActions.setMappedDeliverables({
          deliverables: mapChangeRequestDeliverables(
            deliverables,
            previewDeliverables,
            previewDeliverablesMap,
            changeItemsMap,
            false
          ),
        }),
        ChangeRequestDeliverableActions.setMappedTrackedDeliverables({
          deliverables: mapChangeRequestDeliverables(
            deliverables,
            previewDeliverables,
            previewDeliverablesMap,
            changeItemsMap,
            true
          ),
        }),
      ])
    )
  )
}
